import { Input, OdinIcon, OdinIconSize } from '@myosh/odin-components'
import { useMemo, useState } from 'react'
import type { GapAnalysisViewDto, TrainingMatrixViewDto } from '../../../API/rtk-query-api/store/tmsApi'
import ActionIcon from '../action-icon/action-icon'

interface ViewNameEditorProps {
  view: TrainingMatrixViewDto | GapAnalysisViewDto
  isEven: boolean
  onChange: (view: TrainingMatrixViewDto | GapAnalysisViewDto) => void
  onRemove: (view: TrainingMatrixViewDto | GapAnalysisViewDto) => void
}

function ViewNameEditor({ view, isEven, onChange, onRemove }: ViewNameEditorProps) {
  const [editMode, setEditMode] = useState(false)
  const [viewName, setViewName] = useState<string>(view.title ?? '')
  const [hasError, setHasError] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)

  const enableEditMode = () => setEditMode(true)
  const disableEditMode = () => {
    setHasError(false)
    setEditMode(false)
    setViewName(view.title ?? '')
  }

  const handleViewNameChange = (value?: string) => {
    setHasError(false)
    setViewName(value || '')
  }

  const removeItem = () => setShowDeleteConfirmation(true)
  const confirmRemoveItem = () => onRemove(view)
  const cancelRemoveItem = () => setShowDeleteConfirmation(false)

  const applyChanges = () => {
    if (viewName.trim().length > 0) {
      setEditMode(false)
      onChange({
        ...view,
        title: viewName.trim()
      })
    } else {
      setHasError(true)
    }
  }

  const inputStyles = useMemo(() => {
    let classes = 'view-name__input'
    if (hasError) {
      classes += ' input-error'
    }

    return classes
  }, [hasError])

  return (
    <div className='view-name-editor__wrapper' data-even={isEven}>
      {showDeleteConfirmation && (
        <div className='delete-confirmation'>
          <div className='delete-confirmation__text'>
            <span>Delete</span>
            <span className='delete-confirmation__title' title={viewName}>{`"${viewName}"`}</span>
            <span>?</span>
          </div>
          <div className='delete-confirmation__actions'>
            <ActionIcon icon='Check' onClick={confirmRemoveItem} iconTheme='default' />
            <ActionIcon icon='X' onClick={cancelRemoveItem} iconTheme='default' />
          </div>
        </div>
      )}
      {!showDeleteConfirmation && (
        <>
          <div className='view-name-editor__leading'>
            {view.global && <OdinIcon icon='Globe' size={OdinIconSize.ExtraSmall} />}
          </div>
          <div className='view-name-editor'>
            {editMode ? (
              <Input
                value={viewName}
                name='view-edit-input'
                onChange={handleViewNameChange}
                inputStyles={inputStyles}
              />
            ) : (
              <p title={viewName}>{viewName}</p>
            )}
            <div className='view-name-editor__actions'>
              {editMode ? (
                <ActionIcon icon='Check' onClick={applyChanges} hidden />
              ) : (
                <ActionIcon icon='Pencil' onClick={enableEditMode} hidden />
              )}
              {editMode ? (
                <ActionIcon icon='X' onClick={disableEditMode} hidden />
              ) : (
                <ActionIcon icon='Trash' onClick={removeItem} hidden />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default ViewNameEditor
